import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MarketplaceDepositSettings, MarketplaceStatus } from '@spike/marketplace-action';
import {
    MarketplaceDeposit,
    MarketplaceDepositCategory,
    MarketplaceDepositRule,
    MarketplaceDepositType
} from '@spike/marketplace-model';
import { FieldError } from '@spike/model';
import { getEnrollmentStatusThunk, PaymentsStatus } from '@spike/payments-action';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { Switch } from 'components/UI/V3/Forms/Switch';
import { ValueTypeSelector, ValueTypeSelectorValue } from 'components/UI/V3/Forms/ValueTypeSelector';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'store';
import DepositCategoryList from './DepositCategoryList';
import styles from './Deposit.module.scss';
import DepositModeCards from './DepositModeCards';
import {
    DepositApplicability,
    DepositCategoryId,
    DepositCategoryValues,
    selectableRules,
    toCategoryValues,
    toDepositRules,
    toDepositType,
    toSelectedCategories,
    toValueTypeSelectorValue
} from './types';

export interface Props {
    deposit: MarketplaceDeposit;
    depositRules: Array<MarketplaceDepositRule>;
    depositCategories: Array<MarketplaceDepositCategory>;
    onSave?: (settings: MarketplaceDepositSettings) => void;
    onContinue?: (settings: MarketplaceDepositSettings) => void;
    onChange?: (settings: MarketplaceDepositSettings) => void;
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

const SERVER_FIELD_NAMES: Record<string, string> = {
    deposit_value: 'deposit',
    deposit_category: 'deposit',
    categories: 'categories',
    category_id: 'categories'
};

export const Deposit: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const categories = props.depositCategories;

    const savedRules = useMemo(
        () => selectableRules(props.depositRules, props.depositCategories),
        [props.depositRules, props.depositCategories]
    );

    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const depositErrors = useSelector<RootState, Array<FieldError>>(state => state.marketplace.depositErrors);
    const paymentStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const enrollmentStatus = useSelector<RootState, boolean | undefined>(
        state => state.payments.enrollmentStatus?.enabled
    );

    const [saving, setSaving] = useState(false);
    const [depositValue, setDepositValue] = useState<ValueTypeSelectorValue>(() =>
        toValueTypeSelectorValue(props.deposit.type, props.deposit.value)
    );
    const [enabled, setEnabled] = useState(props.deposit.type !== MarketplaceDepositType.None.toString());
    const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [showAlert, setShowAlert] = useState(false);
    const [applicability, setApplicability] = useState<DepositApplicability>(props.deposit.applicability);
    const [selectedCategories, setSelectedCategories] = useState<Array<DepositCategoryId>>(() =>
        toSelectedCategories(savedRules)
    );
    const [categoryValues, setCategoryValues] = useState<DepositCategoryValues>(() => toCategoryValues(savedRules));
    const [newClientDeposits, setNewClientDeposits] = useState(props.deposit.newClientsOnly);

    useEffect(() => {
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));
    }, []);

    useNonInitialEffect(() => {
        switch (status) {
            case MarketplaceStatus.SaveDepositSettingsSuccess:
                setSaving(false);
                break;
            case MarketplaceStatus.SaveDepositSettingsError:
            case MarketplaceStatus.Error:
                setSaving(false);
                break;
        }
    }, [status]);

    useNonInitialEffect(() => {
        setErrors(
            depositErrors.map(error => ({
                fieldName: SERVER_FIELD_NAMES[error.fieldName] ?? error.fieldName,
                errorMessage: error.errorMessage
            }))
        );
    }, [depositErrors]);

    useNonInitialEffect(() => {
        if (paymentStatus === PaymentsStatus.GetEnrollmentStatusSuccess) {
            setShowAlert(!enrollmentStatus);
        }
    }, [paymentStatus]);

    const depositError = errors.find(error => error.fieldName === 'deposit');
    const categoriesError = errors.find(error => error.fieldName === 'categories');

    const buildSettings = (
        value: ValueTypeSelectorValue,
        type: MarketplaceDepositType = toDepositType(value)
    ): MarketplaceDepositSettings => ({
        applicability,
        type,
        value: type === MarketplaceDepositType.None ? undefined : value.value.toString(),
        newClientsOnly: newClientDeposits,
        rules: toDepositRules(selectedCategories, categoryValues)
    });

    const saveDisabled =
        applicability === 'specific'
            ? selectedCategories.length === 0 || selectedCategories.some(id => (categoryValues[id]?.value ?? 0) <= 0)
            : depositValue.value <= 0;

    const validate = (value: ValueTypeSelectorValue): Array<FieldError> => {
        const errors: Array<FieldError> = [];

        if (applicability === 'specific') {
            return errors;
        }

        if (value.value <= 0) {
            errors.push({
                fieldName: 'deposit',
                errorMessage: 'A value greater than 0 is required'
            });
        } else if (value.type === 'percentage' && value.value > 100) {
            errors.push({
                fieldName: 'deposit',
                errorMessage: 'Percentage cannot be greater than 100.'
            });
        }

        return errors;
    };

    const changeApplicabilityHandler = (value: DepositApplicability) => {
        setApplicability(value);
        setErrors([]);
    };

    const changeCategoryCheckedHandler = useCallback((id: DepositCategoryId, checked: boolean) => {
        setSelectedCategories(current =>
            checked ? [...current, id] : current.filter(selectedId => selectedId !== id)
        );

        if (!checked) {
            setCategoryValues(current =>
                Object.entries(current).reduce(
                    (values: DepositCategoryValues, [categoryId, value]) =>
                        categoryId === id ? values : { ...values, [categoryId]: value },
                    {}
                )
            );
        }
    }, []);

    const changeCategoryValueHandler = useCallback((id: DepositCategoryId, value: ValueTypeSelectorValue) => {
        setCategoryValues(current => ({ ...current, [id]: value }));
    }, []);

    const changeEnableHandler = (checked: boolean) => {
        if (checked) {
            setEnabled(checked);
        } else {
            setShowDisableConfirmation(true);
        }
    };

    const disableHandler = () => {
        const disabledValue: ValueTypeSelectorValue = { type: 'amount', value: 0 };
        const settings = buildSettings(disabledValue, MarketplaceDepositType.None);

        setDepositValue(disabledValue);
        props.onChange && props.onChange(settings);
        props.onSave && props.onSave(settings);
        setEnabled(false);
        setShowDisableConfirmation(false);
        setSaving(true);
    };

    const changeValueHandler = useCallback(
        (value: ValueTypeSelectorValue) => {
            setDepositValue(value);
            props.onChange && props.onChange(buildSettings(value));
        },
        [props.onChange, applicability, newClientDeposits, selectedCategories, categoryValues]
    );

    const saveHandler = () => {
        const errors = validate(depositValue);

        setErrors(errors);

        if (errors.length === 0) {
            const settings = buildSettings(depositValue);

            setSaving(true);
            props.onSave && props.onSave(settings);
            props.onContinue && props.onContinue(settings);
        }
    };

    return (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.headerContainer}>
                <div className={styles.titleContainer}>
                    <p className={styles.title}>
                        {props.isMobile && (
                            <FontAwesomeIcon
                                className={styles.iconBusinessTitle}
                                onClick={props.openSidebar}
                                icon={faArrowLeft}
                                size="2x"
                            />
                        )}
                        Deposits
                    </p>
                    <p className={styles.subtitle}>
                        Choose if you would like to ask clients for a deposit at the time of booking.
                    </p>
                </div>
                {!showAlert && (
                    <div className={styles.switchContainer}>
                        <Switch
                            checked={enabled}
                            onChange={changeEnableHandler}
                        />
                    </div>
                )}
            </div>

            {showDisableConfirmation && (
                <GenericModal
                    className={styles.confirmModal}
                    footer={
                        <div className={styles.confirmFooter}>
                            <Button
                                label="Cancel"
                                variant="outlined"
                                fullWidth
                                onClick={() => setShowDisableConfirmation(false)}
                            />
                            <Button
                                label="Remove"
                                variant="black"
                                fullWidth
                                onClick={disableHandler}
                            />
                        </div>
                    }
                    onClose={() => setShowDisableConfirmation(false)}
                >
                    <p className={styles.confirmTitle}>Deposit</p>
                    <p className={styles.confirmQuestion}>
                        Do you want to <strong>remove</strong> the deposit you
                        <br /> previously set for this marketplace?
                    </p>
                </GenericModal>
            )}

            {showAlert && (
                <div className={styles.alertContainer}>
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        className={styles.icon}
                    />
                    <div>
                        <p className={styles.alertText}>
                            CuddlesPay is not active on your account. Click below to enroll in CuddlesPay &amp; begin
                            collecting deposits.
                        </p>
                        <Link to="/payments">
                            <Button
                                label="Add CuddlesPay"
                                variant="green"
                            />
                        </Link>
                    </div>
                </div>
            )}

            {enabled && !showAlert && (
                <>
                    <hr className={styles.headerDivider} />

                    <DepositModeCards
                        className={styles.modeCards}
                        value={applicability}
                        onChange={changeApplicabilityHandler}
                    />
                    {applicability === 'all' && (
                        <p className={styles.subtitleTerciary}>
                            Set a fixed amount or percentage that the customer must pay when booking.
                        </p>
                    )}

                    {applicability === 'specific' ? (
                        <DepositCategoryList
                            className={styles.categoryList}
                            categories={categories}
                            selected={selectedCategories}
                            values={categoryValues}
                            onCheckedChange={changeCategoryCheckedHandler}
                            onValueChange={changeCategoryValueHandler}
                        />
                    ) : (
                        <div className={styles.depositContainer}>
                            <ValueTypeSelector
                                id="deposit"
                                className={styles.depositField}
                                value={depositValue}
                                placeholder="$10"
                                onChange={changeValueHandler}
                            />
                        </div>
                    )}

                    {depositError && <p className={styles.errorMessage}>{depositError.errorMessage}</p>}
                    {categoriesError && <p className={styles.errorMessage}>{categoriesError.errorMessage}</p>}

                    <div className={styles.newClientRow}>
                        <div>
                            <p className={styles.newClientLabel}>New Client Deposits</p>
                            <p className={styles.newClientDescription}>Require deposits for new clients only</p>
                        </div>
                        <Switch
                            checked={newClientDeposits}
                            onChange={setNewClientDeposits}
                        />
                    </div>

                    <div
                        className={clsx(styles.buttonContainer, {
                            [styles.right]: props.onContinue
                        })}
                    >
                        <Button
                            label={props.onSave ? 'Save' : 'Continue'}
                            className={styles.button}
                            disabled={saveDisabled}
                            isLoading={saving}
                            onClick={saveHandler}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Deposit;
